import React, { useState, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';

import MuiTypography from '@material-ui/core/Typography';
import MuiPaper from '@material-ui/core/Paper';
import MuiGrid from '@material-ui/core/Grid';
import MuiBox from '@material-ui/core/Box';
import MuiTabs from '@material-ui/core/Tabs';
import MuiTab from '@material-ui/core/Tab';
import MuiButton from '@material-ui/core/Button';
import GetAppIcon from '@material-ui/icons/GetApp';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

import ViewWrapper from 'views/ViewWrapper';
import SelectableTable from '../../components/SelectableTable';
import ActiveFilters from '../../components/ActiveFilters';
import ColumnFilter from '../../components/ColumnFilter';
import useColumnFilters from '../../hooks/use-column-filters';

const styles = theme => ({
  paper: {
    overflow: 'hidden',
    padding: theme.spacing(2),
  },
  title: {
    paddingBottom: theme.spacing(1),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  actions: {
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1),
    },
  },
  tableHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  filterContainer: {
    marginBottom: theme.spacing(2),
  },
});

// Mock data for demonstration
const generateMockData = count => {
  const data = [];
  const statuses = ['pending', 'approved', 'rejected'];
  const types = ['subscription', 'one-time', 'refund'];

  for (let i = 1; i <= count; i++) {
    data.push({
      id: `tx-${i}`,
      transactionId: `TX-${100000 + i}`,
      date: new Date(2025, 5, Math.floor(Math.random() * 30) + 1)
        .toISOString()
        .split('T')[0],
      amount: parseFloat((Math.random() * 1000).toFixed(2)),
      status: statuses[Math.floor(Math.random() * statuses.length)],
      type: types[Math.floor(Math.random() * types.length)],
      customer: `Customer ${i}`,
    });
  }

  return data;
};

// Column definitions
const columns = [
  { id: 'transactionId', label: 'Transaction ID', minWidth: 120 },
  { id: 'date', label: 'Date', minWidth: 100 },
  {
    id: 'amount',
    label: 'Amount',
    minWidth: 100,
    align: 'right',
    format: value => `$${value.toFixed(2)}`,
  },
  {
    id: 'status',
    label: 'Status',
    minWidth: 100,
    format: value => {
      const statusStyles = {
        pending: { color: '#FF9800' },
        approved: { color: '#4CAF50' },
        rejected: { color: '#F44336' },
      };
      return <span style={statusStyles[value] || {}}>{value}</span>;
    },
  },
  { id: 'type', label: 'Type', minWidth: 100 },
  { id: 'customer', label: 'Customer', minWidth: 150 },
];

// Filter options for select type filters
const statusFilterOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const typeFilterOptions = [
  { value: 'subscription', label: 'Subscription' },
  { value: 'one-time', label: 'One-time' },
  { value: 'refund', label: 'Refund' },
];

/**
 * Financials module main component
 * Implements multi-selection and advanced column filters
 */
const Financials = ({ ...props }) => {
  const { classes } = props;
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Initialize column filters
  const {
    filters,
    tempFilters,
    openFilterId,
    anchorEl,
    handleOpenFilter,
    handleApplyFilter,
    handleClearFilter,
    handleClearAllFilters,
    handleFilterValueChange,
    hasActiveFilters,
    getActiveFiltersCount,
    formatFiltersForApi,
  } = useColumnFilters();

  // Load mock data
  useEffect(() => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setData(generateMockData(100));
      setLoading(false);
    }, 500);
  }, []);

  // Filter data based on active filters
  const filteredData = React.useMemo(() => {
    if (!hasActiveFilters) return data;

    return data.filter(row => {
      // Check each filter
      return Object.entries(filters).every(([columnId, filterValues]) => {
        const value = row[columnId];

        // Handle different filter types
        switch (columnId) {
          case 'amount':
            if (filterValues.operator === 'equals') {
              return value === parseFloat(filterValues.value);
            } else if (filterValues.operator === 'gt') {
              return value > parseFloat(filterValues.value);
            } else if (filterValues.operator === 'lt') {
              return value < parseFloat(filterValues.value);
            } else if (filterValues.operator === 'between') {
              return (
                value >= parseFloat(filterValues.min) &&
                value <= parseFloat(filterValues.max)
              );
            }
            return true;

          case 'date':
            if (filterValues.operator === 'equals') {
              return value === filterValues.value;
            } else if (filterValues.operator === 'after') {
              return new Date(value) > new Date(filterValues.value);
            } else if (filterValues.operator === 'before') {
              return new Date(value) < new Date(filterValues.value);
            } else if (filterValues.operator === 'between') {
              return (
                new Date(value) >= new Date(filterValues.startDate) &&
                new Date(value) <= new Date(filterValues.endDate)
              );
            }
            return true;

          case 'status':
          case 'type':
            return value === filterValues.value || filterValues.value === '';

          default:
            if (filterValues.operator === 'contains') {
              return value
                .toLowerCase()
                .includes(filterValues.value.toLowerCase());
            } else if (filterValues.operator === 'equals') {
              return value.toLowerCase() === filterValues.value.toLowerCase();
            } else if (filterValues.operator === 'startsWith') {
              return value
                .toLowerCase()
                .startsWith(filterValues.value.toLowerCase());
            } else if (filterValues.operator === 'endsWith') {
              return value
                .toLowerCase()
                .endsWith(filterValues.value.toLowerCase());
            }
            return true;
        }
      });
    });
  }, [data, filters, hasActiveFilters]);

  // Calculate current page data
  const currentPageData = React.useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle row click
  const handleRowClick = (_event, _row) => {
    // Aqui poderia navegar para uma página de detalhes
    // Exemplo: history.push(`/financials/${_row.id}`);
  };

  // Handle selection change
  const [selectedItems, setSelectedItems] = useState([]);
  const handleSelectionChange = items => {
    setSelectedItems(items);
  };

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get filter type based on column id
  const getFilterType = React.useCallback(column => {
    return column.id === 'amount'
      ? 'number'
      : column.id === 'date'
      ? 'date'
      : column.id === 'status' || column.id === 'type'
      ? 'select'
      : 'text';
  }, []);

  // Handle export selected
  const handleExportSelected = items => {
    if (items.length === 0) {
      alert('Selecione pelo menos um item para exportar');
      return;
    }

    // Aqui implementaria a lógica real de exportação
    alert(`Exportando ${items.length} itens selecionados`);

    // Exemplo de como seria a chamada para API:
    // api.exportFinancialItems(items.map(item => item.id))
    //   .then(() => {
    //     showNotification('Itens exportados com sucesso');
    //   })
    //   .catch(error => {
    //     showError('Erro ao exportar itens', error);
    //   });
  };

  // Handle approve selected
  const handleApproveSelected = items => {
    if (items.length === 0) {
      alert('Selecione pelo menos um item para aprovar');
      return;
    }

    // Aqui implementaria a lógica real de aprovação
    alert(`Aprovando ${items.length} itens selecionados`);

    // Exemplo de como seria a chamada para API:
    // api.approveFinancialItems(items.map(item => item.id))
    //   .then(() => {
    //     showNotification('Itens aprovados com sucesso');
    //     // Recarregar dados após aprovação
    //     loadData();
    //   })
    //   .catch(error => {
    //     showError('Erro ao aprovar itens', error);
    //   });
  };

  return (
    <ViewWrapper>
      <MuiGrid container spacing={3}>
        <MuiGrid item xs={12}>
          <MuiPaper className={classes.paper}>
            <MuiBox className={classes.header}>
              <MuiTypography className={classes.title} variant="h5">
                Financial Management
              </MuiTypography>
              <MuiBox className={classes.actions}>
                <MuiButton
                  variant="outlined"
                  color="primary"
                  startIcon={<GetAppIcon />}
                  onClick={() =>
                    handleExportSelected(
                      selectedItems.length > 0 ? selectedItems : data
                    )
                  }
                  disabled={loading}>
                  {selectedItems.length > 0
                    ? `Exportar (${selectedItems.length})`
                    : 'Exportar Todos'}
                </MuiButton>
                <MuiButton
                  variant="contained"
                  color="primary"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => handleApproveSelected(selectedItems)}
                  disabled={selectedItems.length === 0 || loading}>
                  {selectedItems.length > 0
                    ? `Aprovar (${selectedItems.length})`
                    : 'Aprovar'}
                </MuiButton>
              </MuiBox>
            </MuiBox>

            <MuiTabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto">
              <MuiTab label="All Transactions" />
              <MuiTab label="Pending" />
              <MuiTab label="Approved" />
              <MuiTab label="Rejected" />
            </MuiTabs>
          </MuiPaper>
        </MuiGrid>

        <MuiGrid item xs={12}>
          <MuiPaper className={classes.paper}>
            <MuiBox className={classes.tableHeader}>
              <MuiTypography variant="h6">
                Transactions
                {hasActiveFilters && (
                  <span> ({getActiveFiltersCount()} filters applied)</span>
                )}
              </MuiTypography>
            </MuiBox>

            {hasActiveFilters && (
              <MuiBox className={classes.filterContainer}>
                <ActiveFilters
                  filters={filters}
                  columns={columns}
                  onClearFilter={handleClearFilter}
                  onClearAllFilters={handleClearAllFilters}
                />
              </MuiBox>
            )}

            <SelectableTable
              columns={columns.map(column => ({
                ...column,
                label: (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {column.label}
                    <ColumnFilter
                      columnId={column.id}
                      columnLabel={column.label}
                      filterType={getFilterType(column)}
                      hasFilter={!!filters[column.id]}
                      onOpenFilter={handleOpenFilter}
                      onApplyFilter={handleApplyFilter}
                      onClearFilter={() => handleClearFilter(column.id)}
                      filterValues={
                        openFilterId === column.id
                          ? tempFilters
                          : filters[column.id]
                      }
                      onFilterValueChange={handleFilterValueChange}
                      options={
                        column.id === 'status'
                          ? statusFilterOptions
                          : column.id === 'type'
                          ? typeFilterOptions
                          : []
                      }
                    />
                  </div>
                ),
                filter: true,
              }))}
              data={currentPageData}
              onRowClick={handleRowClick}
              getFilterType={getFilterType}
              filterOptions={{
                status: statusFilterOptions,
                type: typeFilterOptions,
              }}
              selectionEnabled
              onSelectionChange={handleSelectionChange}
              batchActions={[
                {
                  label: 'Export',
                  onClick: handleExportSelected,
                  icon: <GetAppIcon />,
                  color: 'primary',
                },
                {
                  label: 'Approve',
                  onClick: handleApproveSelected,
                  icon: <CheckCircleIcon />,
                  color: 'secondary',
                },
              ]}
              page={page}
              rowsPerPage={rowsPerPage}
              totalCount={filteredData.length}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              loading={loading}
              noDataMessage={
                hasActiveFilters
                  ? 'No matching records found'
                  : 'No data available'
              }
            />
          </MuiPaper>
        </MuiGrid>
      </MuiGrid>
    </ViewWrapper>
  );
};

export default withStyles(styles)(Financials);
